import React, { useCallback, useMemo, useState } from 'react';
import { Button, Dialog, Grid, Typography } from '@mui/material';
import { useActivitiesContext } from 'pages/Activities/activities.context';
import { useSnackbarContext } from 'context/snackbar.context';
import styled from 'styled-components';
import { ErrorTextWrapper } from 'pages/Campaigns/UpdateCampaign';
import { hasPermissions } from 'config/ability';
import { useAuthContext } from 'context/auth.context';
import { ActivityAssignmentData } from 'hooks/activities/useQueryActivityAssignments';
import useMutationCompleteActivityAssignment from 'hooks/activities/useMutationCompleteActivityAssignment';
import Stack from '@mui/material/Stack';

const TextWrapper = styled.div`
  margin: 2rem;
`;

interface CompleteActivityAssignmentProps {
  activityAssignment: ActivityAssignmentData;
}

type Props = CompleteActivityAssignmentProps;

const CompleteActivityAssignment = ({ activityAssignment }: Props) => {
  const [showCompleteActivityModal, setShowCompleteActivityModal] = useState<boolean>(false);
  const { displaySnackbarMessage, displayApiResult } = useSnackbarContext();
  const { refetchActivities } = useActivitiesContext();
  const [completeActivityAssignment] = useMutationCompleteActivityAssignment();
  const { ability, userTenantId } = useAuthContext();

  const requiredIncompleteSteps = useMemo(() => {
    const requiredIncompleteSteps: number[] = [];

    activityAssignment?.steps?.forEach(({ step, response }, i) => {
      if (step.isRequired && !response?.completedDate) {
        requiredIncompleteSteps.push(i + 1);
      }
    });
    return requiredIncompleteSteps;
  }, [activityAssignment?.steps]);

  const handleCompleteActivityAssignment = useCallback(
    async (activityAssignment: ActivityAssignmentData) => {
      const result = await completeActivityAssignment({
        variables: {
          input: {
            id: activityAssignment.id,
            completedDate: new Date().toISOString().slice(0, 10),
          },
        },
      }).then(
        (result) => {
          result.data?.completeActivityAssignment &&
            displayApiResult(result.data?.completeActivityAssignment, 'Activity Completed');
          refetchActivities();
        },
        (rejection) => {
          displaySnackbarMessage(rejection.message, 'error');
        }
      );

      setShowCompleteActivityModal(false);
      return result;
    },
    [completeActivityAssignment, displayApiResult, displaySnackbarMessage, refetchActivities]
  );

  // Return null if user does not have 'Complete' permissions
  if (
    !hasPermissions(ability, 'complete', 'Activity', { tenantId: userTenantId }) &&
    !hasPermissions(ability, 'complete', 'Activity', {
      activityId: activityAssignment?.activity?.id,
    }) &&
    !hasPermissions(ability, 'complete', 'ActivityAssignment', {
      activityAssignmentId: activityAssignment?.id,
    })
  ) {
    return null;
  }

  return (
    <>
      {activityAssignment && (
        <>
          <Button
            variant='contained'
            color='secondary'
            onClick={() => setShowCompleteActivityModal(true)}
          >
            Complete Activity
          </Button>
          <Dialog
            open={showCompleteActivityModal}
            onClose={() => setShowCompleteActivityModal(false)}
          >
            <Grid container textAlign='center' p={2}>
              <Grid item xs={12}>
                <Typography variant='h5'>{activityAssignment.activity.subject}</Typography>
                <TextWrapper>Are you sure you want to complete this activity?</TextWrapper>
              </Grid>
              <Grid item xs={12}>
                <Stack direction='row' spacing={2} justifyContent='center'>
                  <Button onClick={() => setShowCompleteActivityModal(false)}>Cancel</Button>
                  <Button
                    disabled={!!requiredIncompleteSteps.length}
                    onClick={() => handleCompleteActivityAssignment(activityAssignment)}
                    variant='contained'
                    color='secondary'
                  >
                    Complete
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12}></Grid>
            </Grid>
            {!!requiredIncompleteSteps.length && (
              <ErrorTextWrapper>
                * Required Step(s) are not Completed: {requiredIncompleteSteps.join(', ')}
              </ErrorTextWrapper>
            )}
          </Dialog>
        </>
      )}
    </>
  );
};

export default CompleteActivityAssignment;
